import {
  computed,
  effect,
  inject,
  Injector,
  runInInjectionContext,
  untracked,
} from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';

import { DeepPartial } from '@apollo/client/utilities';
import { tapResponse } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import {
  catchError,
  concatMap,
  EMPTY,
  from,
  map,
  Observable,
  of,
  switchMap,
  tap,
  toArray,
} from 'rxjs';
import { getTag } from '../../../../_core/_utils/get-tag';
import {
  AbilitaRichiesteGQL,
  AddCerGQL,
  Admin_Richiesta_Pubblicazione_Insert_Input,
  CalcoloRipartizioneGQL,
  Cambia_Stato_Founder_Args,
  CambiaStatoFounderGQL,
  Cer_Bool_Exp,
  Cer_Destinazione_Contributo_Bool_Exp,
  Cer_Geom_Set_Input,
  Cer_Gestore_Insert_Input,
  Cer_Insert_Input,
  Cer_On_Conflict,
  Cer_Order_By,
  ChiediPubblicazioneGQL,
  ClearCerNotificationGQL,
  Exact,
  CambiaStatoCabinaFounderGQL,
  EliminaCabinaFounderGQL,
  GetAssetInCabinaIdsGQL,
  GetCerFounderDocument,
  GetCerFounderSubscription,
  GetCersCountDocument,
  GetCersCountSubscription,
  GetCersDocument,
  GetCersSettingsDocument,
  GetCersSettingsSubscription,
  GetCersSubscription,
  GetConfigurazioneFounderDocument,
  GetConfigurazioneFounderSubscription,
  GetConfigurazioneGeomGQL,
  GetConfigurazioniCerCountDocument,
  GetConfigurazioniCerCountSubscription,
  GetConfigurazioniCerDocument,
  GetConfigurazioniCerGQL,
  GetConfigurazioniCerSubscription,
  GetElencoDatiProduttoreFounderGQL,
  GetElencoIncentiviByCerCabinaIdDocument,
  GetElencoIncentiviByCerCabinaIdSubscription,
  _Sottoscrizione_Tipo_Richiesta_Enum,
  GetExportElencoRichiesteFounderGQL,
  GetExportFlussoConsumatori2026FounderGQL,
  GetExportFlussoProduttoriGseFounderGQL,
  GetExportCustomFounderGQL,
  GetGeomCabinaGQL,
  GetSottoscrizioneFounderDocument,
  GetSottoscrizioneFounderSubscription,
  SearchSottoscrizioniStatiDocument,
  SearchSottoscrizioniStatiSubscription,
  SearchSottoscrizioniCountDocument,
  SearchSottoscrizioniCountSubscription,
  SearchSottoscrizioniDocument,
  SearchSottoscrizioniSubscription,
  // GetStagingLottiGQL,
  // GetStagingLottiQuery,
  // GetStagingSottoscrizioneFounderGQL,
  // GetStagingSottoscrizioneFounderQuery,
  Incentivi_Ripartizione_Insert_Input,
  Incentivi_Ripartizione_On_Conflict,
  InputMaybe,
  Indirizzo_Set_Input,
  InsertOrUpdateCerGQL,
  InsertOrUpdateConfigurazioneGQL,
  InsertOrUpdateIncentivoGQL,
  InsertOrUpdateSottoscrizioneGQL,
  MostraGQL,
  Sottoscrizione_Insert_Input,
  Sottoscrizione_On_Conflict,
  Sottoscrizione_Order_By,
  Energy_Village__Stato_Sottoscrizione_Enum,
  UpdateCerGeomGQL,
  SalvaSnapshotCabinaFounderGQL,
  SetDestinazioniConfigurazioneGQL,
  UpdateCodiceGseGQL,
  UpdatePersonaFounderGQL,
  UpdateContattoFounderGQL,
  UpdateAssetFounderGQL,
  UpdateAssetProduzioneFounderGQL,
  UpdateAssetProduzioneUnitaFounderGQL,
  UpdateAssetConsumoFounderGQL,
  UpdateCondivisioneFounderGQL,
  Persona_Set_Input,
  Contatto_Set_Input,
  Asset_Set_Input,
  Asset_Produzione_Set_Input,
  Asset_Produzione_Unita_Set_Input,
  Asset_Consumo_Set_Input,
  Condivisione_Condivisione_Set_Input,
  GetListaUtentiFounderGQL,
  GetListaUtentiFounderCountGQL,
  GetWhiteLabelsListaUtentiFounderGQL,
  Auth_User_Entity_Bool_Exp,
  Auth_User_Entity_Order_By,
  Indirizzo_Constraint,
  Indirizzo_Update_Column,
  InsertEmailGQL,
  GetEmailTemplatesGQL,
  InsertEmailTemplateGQL,
  DeleteEmailTemplateGQL,
  UpdateEmailTemplateGQL,
  GetEmailInviateGQL,
  GetEmailTemplatesQuery,
  GetEmailInviateQuery,
  Comunicazioni_Email_Bool_Exp,
  Comunicazioni_Email_Order_By,
  Order_By,
  InsertOrUpdatePersonaFounderGQL,
  InsertSmsGQL,
  Persona_Constraint,
  Persona_Update_Column,
  // UpdateStatoStagingSottoscrizioniGQL,
  GetConfigurazioneIdFounderGQL,
  GetPresetFounderGQL,
  InsertOrUpdatePresetFounderGQL,
  DeletePresetFounderGQL,
  Preset_Insert_Input,
  Preset_On_Conflict,
  Preset_Bool_Exp,
} from '../../../../_core/_graphql/generated';
import { ApolloRoleService } from '../../../../_core/_services/apollo-role.service';
import { LoadingService } from '../../../../_core/_services/loading.service';
import { LoghiStore } from '../../../../_core/_signal-store/_loghi/loghi.store';
import { UserStore } from '../../_store/user.store';
import { initialFounderState } from './founder.state';

export const FounderStore = signalStore(
  { providedIn: 'root' },
  withState(initialFounderState),
  withComputed((store) => ({
    badges: computed(() =>
      (store.sottoscrizioni_stati_data?.() ?? [])
        .filter(
          (s) => s !== Energy_Village__Stato_Sottoscrizione_Enum.Cancellata,
        )
        .map((s) => ({ key: s, value: 0 })),
    ),
    sottoscrizioni_active_filter_count: computed(() => {
      const filter = store.sottoscrizioni_active_filter();
      const search = store.sottoscrizioni_search();
      let count = 0;
      if (filter?.configurazioni?.length) count += filter.configurazioni.length;
      if (filter?.cabina?.length) count += filter.cabina.length;
      if (filter?.stato?.length) count += filter.stato.length;
      if (filter?.richiesta?.length) count += filter.richiesta.length;
      if (filter?.nascondi?.length) count += 1;
      if (filter?.codice_presentatore?.trim()) count += 1;
      if (filter?.codice_personale?.trim()) count += 1;
      if (filter?.data_inserimento_da) count += 1;
      if (filter?.data_inserimento_a) count += 1;
      if (search?.trim()) count += 1;
      return count;
    }),
    cers_active_filter_count: computed(() => {
      const filter = store.cers_active_filter();
      let count = 0;
      if (store.cers_search()?.trim()) count += 1;
      if (filter?.cer?.richieste) count += 1;
      if (filter?.stato?.length) count += filter.stato.length;
      return count;
    }),
    configurazioni_active_filter_count: computed(() => {
      const filter = store.configurazioni_active_filter();
      let count = 0;
      if (store.configurazioni_search()?.trim()) count += 1;
      if (filter?.cabina?.length) count += 1;
      if (filter?.pubblicate) count += 1;
      if (filter?.visibili) count += 1;
      if (filter?.richieste_abilitate) count += 1;
      return count;
    }),
  })),
  withMethods((store, injector = inject(Injector)) => {
    // Registry in-memory (non nel state serializzato)
    const subs = new Map<string, { unsubscribe: () => void }>();

    const setSub = (key: string, sub: { unsubscribe: () => void }) => {
      // se esiste, chiudi quella precedente
      subs.get(key)?.unsubscribe?.();
      subs.set(key, sub);
    };

    // Scope dei modelli email, come i preset dell'export: whitelabel (condivisi) o account (personali)
    const emailTemplateScope = (): Observable<{
      configurazione_id: string | null;
      auth_id: string | null;
    }> =>
      runInInjectionContext(injector, () => {
        const auth_id = inject(UserStore).user_data?.()?.sub ?? null;
        const tag = getTag();
        if (!tag) return of({ configurazione_id: null, auth_id });
        return inject(GetConfigurazioneIdFounderGQL)
          .fetch(
            { referral: tag },
            { context: { headers: { 'x-hasura-role': 'manager' } } },
          )
          .pipe(
            map((res) => ({
              configurazione_id:
                res.data?.white_label_configurazione?.[0]?.id ?? null,
              auth_id,
            })),
            // se la whitelabel non si risolve restano comunque i modelli personali
            catchError(() => of({ configurazione_id: null, auth_id })),
          );
      });

    const clearSub = (key: string) => {
      subs.get(key)?.unsubscribe?.();
      subs.delete(key);
    };

    const clearAllSubs = (prefix?: string) => {
      for (const [k, s] of subs.entries()) {
        if (!prefix || k.startsWith(prefix)) {
          s?.unsubscribe?.();
          subs.delete(k);
        }
      }
    };

    // Accetta Date (datepicker) o stringa ISO (preset salvati) e restituisce 'yyyy-MM-dd' in ora locale.
    const asDataIso = (v: unknown): string | null => {
      if (!v) return null;
      const d = v instanceof Date ? v : new Date(String(v));
      if (isNaN(d.getTime())) return null;
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${d.getFullYear()}-${mm}-${dd}`;
    };
    const addGiorno = (iso: string): string => {
      const [y, m, d] = iso.split('-').map(Number);
      const next = new Date(y, m - 1, d + 1);
      return asDataIso(next)!; // sempre valida: costruita da numeri
    };

    const buildSottoscrizioniWhere = (filter?: any, search?: string) => {
      const nascondi: string[] = filter?.nascondi ?? defaultNascondi;
      const statiDaEscludere: string[] = [
        ...(nascondi.includes('nascondi_recessi') ? statiRecesso : []),
        ...(nascondi.includes('nascondi_rifiutati') ? statiRifiutati : []),
      ];

      let orStato: any[] =
        filter?.stato?.length > 0
          ? filter.stato.map((e: any) => ({ stato: { _eq: e } }))
          : [];
      if (statiDaEscludere.length > 0 && orStato.length > 0) {
        orStato = orStato.filter(
          (cond: any) => !statiDaEscludere.includes(cond.stato._eq),
        );
      }

      const orRichiesta: any[] =
        filter?.richiesta?.length > 0
          ? filter.richiesta.map((e: any) => ({
            tipologia_richiesta: { _eq: e },
          }))
          : [];
      const orSearch: any[] = search?.trim()
        ? [
          { asset_consumo: { pod: { _ilike: `%${search.trim()}%` } } },
          // { asset_consumo: { intestatario: { nome: { _ilike: `%${search.trim()}%` } } } },
          // { asset_consumo: { intestatario: { cognome: { _ilike: `%${search.trim()}%` } } } },
          // { asset_consumo: { intestatario: { ragione_sociale: { _ilike: `%${search.trim()}%` } } } },
          {
            asset_consumo: {
              asset: {
                gestori: {
                  utente: { username: { _ilike: `%${search.trim()}%` } },
                },
              },
            },
          },
          // { asset_consumo: { asset: { gestori: { utente: { first_name: { _ilike: `%${search.trim()}%` } } } } } },
          // { asset_consumo: { asset: { gestori: { utente: { last_name: { _ilike: `%${search.trim()}%` } } } } } },
        ]
        : [];
      // if (search?.trim()) {
      //   const tokens = search.trim().split(/\s+/);
      //   if (tokens.length > 1) {
      //     orSearch.push({
      //       asset_consumo: {
      //         intestatario: {
      //           _and: tokens.map((token: string) => ({
      //             _or: [
      //               { nome: { _ilike: `%${token}%` } },
      //               { cognome: { _ilike: `%${token}%` } },
      //             ],
      //           })),
      //         },
      //       },
      //     });
      //     orSearch.push({
      //       asset_consumo: {
      //         asset: {
      //           gestori: {
      //             utente: {
      //               _and: tokens.map((token: string) => ({
      //                 _or: [
      //                   { first_name: { _ilike: `%${token}%` } },
      //                   { last_name: { _ilike: `%${token}%` } },
      //                 ],
      //               })),
      //             },
      //           },
      //         },
      //       },
      //     });
      //   }
      // }
      const statoNin: any[] =
        statiDaEscludere.length > 0 && orStato.length === 0
          ? [{ stato: { _nin: [...statiDaEscludere, Energy_Village__Stato_Sottoscrizione_Enum.Cancellata] } }]
          : [];

      // Codici amico: `_ilike` senza wildcard (asCodice li scarta) = uguale case-insensitive
      const codicePresentatore = asCodice(filter?.codice_presentatore);
      const codicePersonale = asCodice(filter?.codice_personale);
      const andCodici: any[] = [
        ...(codicePresentatore
          ? [{ codice_univico_promotore: { _ilike: codicePresentatore } }]
          : []),
        ...(codicePersonale
          ? [
              {
                asset_consumo: {
                  asset: {
                    gestori: {
                      utente: {
                        codice_operatore: {
                          codice: { _ilike: codicePersonale },
                        },
                      },
                    },
                  },
                },
              },
            ]
          : []),
      ];

      // Data inserimento (created_at): intervallo da/a inclusivo; "a" copre l'intera giornata.
      const dataDa = asDataIso(filter?.data_inserimento_da);
      const dataA = asDataIso(filter?.data_inserimento_a);
      const andData: any[] = [
        ...(dataDa ? [{ created_at: { _gte: dataDa } }] : []),
        ...(dataA
          ? [{ created_at: { _lt: addGiorno(dataA) } }]
          : []),
      ];

      return {
        ...(filter?.cabina?.length > 0
          ? { cer_cabina: { cabina_id: { _in: filter.cabina } } }
          : {}),
        ...(filter?.configurazioni?.length > 0
          ? { cer_cabina: { cer: { id: { _in: filter.configurazioni } } } }
          : {}),
        ...(orStato.length > 0 ||
        orRichiesta.length > 0 ||
        orSearch.length > 0 ||
        statoNin.length > 0 ||
        andCodici.length > 0 ||
        andData.length > 0
          ? {
              _and: [
                ...(orStato.length > 0 ? [{ _or: orStato }] : []),
                ...(orRichiesta.length > 0 ? [{ _or: orRichiesta }] : []),
                ...(orSearch.length > 0 ? [{ _or: orSearch }] : []),
                ...statoNin,
                ...andCodici,
                ...andData,
              ],
            }
          : {}),
      };
    };

    const uuidRe =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const asUuid = (v: any): string | null => {
      const candidate =
        typeof v === 'string'
          ? v
          : typeof v?.id === 'string'
            ? v.id
            : typeof v?.value === 'string'
              ? v.value
              : typeof v?.key === 'string'
                ? v.key
                : null;
      if (!candidate) return null;
      const x = candidate.trim();
      return uuidRe.test(x) ? x : null;
    };
    const asInt = (v: any): number | null => {
      const candidate =
        typeof v === 'number'
          ? v
          : typeof v === 'string'
            ? Number(v)
            : typeof v?.id === 'number'
              ? v.id
              : typeof v?.id === 'string'
                ? Number(v.id)
                : typeof v?.value === 'number'
                  ? v.value
                  : typeof v?.value === 'string'
                    ? Number(v.value)
                    : null;
      return Number.isInteger(candidate) ? candidate : null;
    };
    const asText = (v: any): string | null => {
      const candidate =
        typeof v === 'string'
          ? v
          : typeof v?.value === 'string'
            ? v.value
            : typeof v?.key === 'string'
              ? v.key
              : typeof v?.id === 'string'
                ? v.id
                : null;
      if (!candidate) return null;
      const x = candidate.trim();
      return x.length > 0 ? x : null;
    };
    // 🔥 Codici amico: 5 caratteri alfanumerici maiuscoli. Si accetta solo [A-Z0-9] così non passano
    // wildcard (%, _) negli `_ilike` del where e il confronto resta esatto.
    const asCodice = (v: any): string | null => {
      const text = asText(v)?.toUpperCase();
      return text && /^[A-Z0-9]+$/.test(text) ? text : null;
    };
    const defaultNascondi = ['nascondi_recessi', 'nascondi_rifiutati'];
    const statiRecesso: string[] = [
      Energy_Village__Stato_Sottoscrizione_Enum.Recessa,
      Energy_Village__Stato_Sottoscrizione_Enum.RecessoRichiesto,
      Energy_Village__Stato_Sottoscrizione_Enum.DisdettaRichiesta,
    ];
    const statiRifiutati: string[] = [
      Energy_Village__Stato_Sottoscrizione_Enum.Rifiutata,
    ];

    const buildListaUtentiWhere = (filter?: {
      whitelabel?: string[];
      tipologia_profilo?: string[];
    }): Auth_User_Entity_Bool_Exp => {
      const andParts: Auth_User_Entity_Bool_Exp[] = [];
      const whitelabel = (filter?.whitelabel ?? []).filter(Boolean);
      const tipologie = (filter?.tipologia_profilo ?? []).filter(Boolean);

      if (whitelabel.length > 0) {
        andParts.push({
          user_attributes: {
            white_label: {
              referral: { _in: whitelabel },
            },
          },
        });
      }

      if (tipologie.length > 0) {
        andParts.push({
          profili: {
            tipologia: { _in: tipologie as any },
          },
        });
      }

      return andParts.length > 0 ? { _and: andParts } : {};
    };

    const buildSottoscrizioniArgs = (filter?: any, search?: string) => {
      const p_cabina = (filter?.cabina ?? [])
        .map(asInt)
        .filter((v: number | null): v is number => v != null);
      const p_configurazioni = (filter?.configurazioni ?? [])
        .map(asUuid)
        .filter((v: string | null): v is string => v != null);
      let p_stato = (filter?.stato ?? [])
        .map(asText)
        .filter((v: string | null): v is string => v != null) as string[];
      const p_richiesta = (filter?.richiesta ?? [])
        .map(asText)
        .filter((v: string | null): v is string => v != null);

      const nascondi: string[] = filter?.nascondi ?? defaultNascondi;
      const nascondiRecessi = nascondi.includes('nascondi_recessi');
      const nascondiRifiutati = nascondi.includes('nascondi_rifiutati');
      const statiDaEscludere: string[] = [
        ...(nascondiRecessi ? statiRecesso : []),
        ...(nascondiRifiutati ? statiRifiutati : []),
      ];

      if (statiDaEscludere.length > 0) {
        if (p_stato.length > 0) {
          p_stato = p_stato.filter((s) => !statiDaEscludere.includes(s));
        } else {
          p_stato = Object.values(
            Energy_Village__Stato_Sottoscrizione_Enum,
          ).filter(
            (s) =>
              !statiDaEscludere.includes(s) &&
              s !== Energy_Village__Stato_Sottoscrizione_Enum.Cancellata,
          );
        }
      }

      return {
        p_search: search?.trim() ? search.trim() : null,
        p_stato: p_stato.length > 0 ? p_stato : null,
        p_richiesta: p_richiesta.length > 0 ? p_richiesta : null,
        p_cabina: p_cabina.length > 0 ? p_cabina : null,
        p_configurazioni: p_configurazioni.length > 0 ? p_configurazioni : null,
        p_codice_presentatore: asCodice(filter?.codice_presentatore),
        p_codice_personale: asCodice(filter?.codice_personale),
        p_data_da: asDataIso(filter?.data_inserimento_da),
        p_data_a: asDataIso(filter?.data_inserimento_a),
      };
    };
    const toPgArrayText = (values: string[]): string | null => {
      if (!values || values.length === 0) return null;
      const escaped = values.map(
        (v) => `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`,
      );
      return `{${escaped.join(',')}}`;
    };
    const toPgArrayNumber = (values: number[]): string | null => {
      if (!values || values.length === 0) return null;
      return `{${values.join(',')}}`;
    };
    const sanitizeSottoscrizioniArgs = (args: any) => {
      const cfg = Array.isArray(args?.p_configurazioni)
        ? args.p_configurazioni
          .filter((v: any) => typeof v === 'string')
          .map((v: string) => v.trim())
          .filter((v: string) => uuidRe.test(v))
        : [];
      const cab = Array.isArray(args?.p_cabina)
        ? args.p_cabina
          .map((v: any) => Number(v))
          .filter((v: number) => Number.isInteger(v))
        : [];
      const stato = Array.isArray(args?.p_stato)
        ? args.p_stato
          .filter((v: any) => typeof v === 'string' && v.trim().length > 0)
          .map((v: string) => v.trim())
        : [];
      const richiesta = Array.isArray(args?.p_richiesta)
        ? args.p_richiesta
          .filter((v: any) => typeof v === 'string' && v.trim().length > 0)
          .map((v: string) => v.trim())
        : [];
      const p_search =
        typeof args?.p_search === 'string' && args.p_search.trim().length > 0
          ? args.p_search.trim()
          : null;
      return {
        p_search,
        p_stato: toPgArrayText(stato),
        p_richiesta: toPgArrayText(richiesta),
        p_cabina: toPgArrayNumber(cab),
        p_configurazioni: toPgArrayText(cfg),
        p_codice_presentatore: asCodice(args?.p_codice_presentatore),
        p_codice_personale: asCodice(args?.p_codice_personale),
        p_data_da: asDataIso(args?.p_data_da),
        p_data_a: asDataIso(args?.p_data_a),
      };
    };

    return {
      initCersSettings: (filter?: any) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          patchState(store, {
            settings_loading: true,
          });

          // chiudi eventuale sub precedente per questa chiave logica
          clearSub('cersSettings');

          const sub = _getter
            .subscribe<GetCersSettingsSubscription>({
              query: GetCersSettingsDocument,
              variables: {},
            })
            .subscribe({
              next: (res) => {
                patchState(store, {
                  settings_loading: false,
                  settings_data: res.data?.cer,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  settings_loading: false,
                  settings_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          // registra la nuova sub
          setSub('cersSettings', sub);
          return sub; // se vuoi poterci interagire a valle
        }),

      initCers: (filter?: any, search?: string) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const pageSize = store.cers_page_size();
          const resolvedSearch = search !== undefined ? search : store.cers_search();

          const p_stato = Array.isArray(filter?.stato) ? filter.stato : [];
          const args = {
            p_search: resolvedSearch?.trim() || null,
            p_only_root: true,
            p_stato: toPgArrayText(p_stato),
            p_richieste_gt_zero: filter?.cer?.richieste ? true : null,
          };
          const hasFilterConditions = !!(args.p_richieste_gt_zero || p_stato?.length);
          const where: Cer_Bool_Exp = {};

          patchState(store, {
            cers_filter: !!(args.p_search || hasFilterConditions),
            cers_active_filter: hasFilterConditions ? filter : undefined,
            cers_search: resolvedSearch,
            cers_page: 0,
            cers_loading: true,
          });

          clearSub('cers');
          const sub = _getter
            .subscribe<GetCersSubscription>({
              query: GetCersDocument,
              variables: {
                args,
                where,
                limit: pageSize,
                offset: 0,
                order_by: store.cers_order_by(),
              } as any,
            })
            .subscribe({
              next: (res) => {
                res?.data?.cer?.map((c) => { _loghi.getLogo(c?.logo?.id); });
                patchState(store, {
                  cers_loading: false,
                  cers_data: res.data?.cer,
                });
              },
              error: (error: any) => {
                patchState(store, { cers_loading: false, cers_data: undefined, failure: error });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('cers', sub);

          clearSub('cersCount');
          const countSub = _getter
            .subscribe<GetCersCountSubscription>({
              query: GetCersCountDocument,
              variables: { args, where } as any,
            })
            .subscribe({
              next: (res) => {
                patchState(store, { cers_total: res.data?.cer_aggregate?.aggregate?.count ?? 0 });
              },
              error: () => { },
            });
          setSub('cersCount', countSub);

          return sub;
        }),

      changeCersPage: (page: number, requestedPageSize?: number) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');
          const _loghi = inject(LoghiStore);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          const filter = store.cers_active_filter();
          const pageSize = requestedPageSize ?? store.cers_page_size();
          const offset = page * pageSize;

          const search = store.cers_search();
          const p_stato = filter?.stato
            ? Object.keys(filter.stato).filter((k: string) => !!filter.stato[k])
            : null;
          const args = {
            p_search: search?.trim() || null,
            p_only_root: true,
            p_stato: p_stato?.length ? p_stato : null,
            p_richieste_gt_zero: filter?.cer?.richieste ? true : null,
          };
          const where: Cer_Bool_Exp = {};

          patchState(store, { cers_page: page, cers_page_size: pageSize });

          clearSub('cers');
          const sub = _getter
            .subscribe<GetCersSubscription>({
              query: GetCersDocument,
              variables: { args, where, limit: pageSize, offset, order_by: store.cers_order_by() } as any,
            })
            .subscribe({
              next: (res) => {
                res?.data?.cer?.map((c) => { _loghi.getLogo(c?.logo?.id); });
                patchState(store, { cers_loading: false, cers_data: res.data?.cer });
              },
              error: (error: any) => {
                patchState(store, { cers_loading: false, cers_data: undefined, failure: error });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('cers', sub);
          return sub;
        }),

      changeCersSort: (order_by: Cer_Order_By[]) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');
          const _loghi = inject(LoghiStore);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          const filter = store.cers_active_filter();
          const pageSize = store.cers_page_size();

          const search = store.cers_search();
          const p_stato = filter?.stato
            ? Object.keys(filter.stato).filter((k: string) => !!filter.stato[k])
            : null;
          const args = {
            p_search: search?.trim() || null,
            p_only_root: true,
            p_stato: p_stato?.length ? p_stato : null,
            p_richieste_gt_zero: filter?.cer?.richieste ? true : null,
          };
          const where: Cer_Bool_Exp = {};

          patchState(store, { cers_order_by: order_by, cers_page: 0, cers_loading: true });

          clearSub('cers');
          const sub = _getter
            .subscribe<GetCersSubscription>({
              query: GetCersDocument,
              variables: { args, where, limit: pageSize, offset: 0, order_by } as any,
            })
            .subscribe({
              next: (res) => {
                res?.data?.cer?.map((c) => { _loghi.getLogo(c?.logo?.id); });
                patchState(store, { cers_loading: false, cers_data: res.data?.cer });
              },
              error: (error: any) => {
                patchState(store, { cers_loading: false, cers_data: undefined, failure: error });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('cers', sub);
        }),

      saveCer: (data: Cer_Gestore_Insert_Input) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(AddCerGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _userStore = inject(UserStore);
          const _loading = inject(LoadingService);
          _loading.start();
          return _mutation
            .mutate(
              {
                objects: { ...data, auth_id: _userStore.user_data?.()?.sub },
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (e) => {
                  return e.data?.insert_cer_gestore?.returning[0];
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },

      insertOrUpdateCer: (
        objects: Cer_Insert_Input,
        on_conflict: Cer_On_Conflict,
        delete_destinazioni?: Cer_Destinazione_Contributo_Bool_Exp,
      ) => {
        return runInInjectionContext(injector, () => {
          const _insertOrUpdate = inject(InsertOrUpdateCerGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);
          return _insertOrUpdate
            .mutate(
              {
                objects,
                on_conflict,
                ...(delete_destinazioni ? { delete_destinazioni } : {}),
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (e) => {
                  if (
                    e.data?.insert_cer?.returning[0].id ==
                    store.selected_cer?.()?.id
                  ) {
                    patchState(store, {
                      selected_cer: e.data?.insert_cer?.returning[0],
                    });
                  }

                  return e.data?.insert_cer;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },
      chiediPubblicazione: (
        object: Admin_Richiesta_Pubblicazione_Insert_Input,
      ) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(ChiediPubblicazioneGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return _mutation
            .mutate(
              {
                cer_id: object.cer_id,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  return res;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },

      loadSelectedCer: (id: string) =>
        runInInjectionContext(injector, () => {
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');

          patchState(store, { selected_cer_loading: true });

          clearSub('selectedCer');

          const sub = _getter
            .subscribe<GetCerFounderSubscription, Exact<{ id?: string }>>({
              query: GetCerFounderDocument,
              variables: {
                id,
              },
            })
            .subscribe({
              next: (res) => {
                _loghi.getLogo(res?.data?.cer_by_pk?.logo?.id);
                patchState(store, {
                  selected_cer: res.data?.cer_by_pk,
                  selected_cer_loading: false,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  selected_cer: undefined,
                  selected_cer_loading: false,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          // registra la nuova sub
          setSub('selectedCer', sub);
          return sub; // se vuoi poterci interagire a valle
        }),

      clearSelectedCer: () => {
        clearSub('selectedCer');
        clearSub('selectedConfigurazione');
        clearSub('configurazioni');
        clearSub('configurazioniCount');

        patchState(store, {
          selected_cer_loading: true,
          selected_cer: undefined,
          configurazioni_loading: true,
          configurazioni_data: undefined,
          configurazioni_page: 0,
          configurazioni_total: 0,
          configurazioni_search: '',
          selected_configurazione_loading: true,
          selected_configurazione: undefined,
          selected_configurazione_geometria: undefined,
          selected_configurazione_geometrie: {},
          selected_configurazione_assets: [],

          selected_configurazione_incentivi_loading: true,
          selected_configurazione_incentivi_data: undefined,

          selected_configurazione_selected_cabina_loading: true,
          selected_configurazione_selected_cabina: undefined,
        });
      },

      initConfigurazioni: (root_id: string, search?: string, filter?: any) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const pageSize = store.configurazioni_page_size();
          const resolvedSearch = search !== undefined ? search : store.configurazioni_search();
          const resolvedFilter = filter !== undefined ? filter : store.configurazioni_active_filter();

          const andFilters: Cer_Bool_Exp[] = [
            ...(resolvedFilter?.pubblicate ? [{ richieste_pubblicazione: { stato: { _neq: 'inviata' } } } as Cer_Bool_Exp] : []),
            ...(resolvedFilter?.visibili ? [{ mostra: { _eq: true } } as Cer_Bool_Exp] : []),
            ...(resolvedFilter?.richieste_abilitate ? [{ abilita_richieste: { _eq: true } } as Cer_Bool_Exp] : []),
            ...(resolvedFilter?.cabina?.length ? [{ cer_cabina: { cabina_id: { _in: resolvedFilter.cabina } } } as Cer_Bool_Exp] : []),
          ];

          const searchFilter: Cer_Bool_Exp[] = resolvedSearch?.trim()
            ? [{ alias: { _ilike: `%${resolvedSearch.trim()}%` } }]
            : [];

          const where: Cer_Bool_Exp = {
            root_id: { _eq: root_id },
            ...(andFilters.length > 0 ? { _and: andFilters } : {}),
            ...(searchFilter.length > 0 ? { _or: searchFilter } : {}),
          };

          patchState(store, {
            configurazioni_loading: true,
            configurazioni_search: resolvedSearch,
            configurazioni_filter: andFilters.length > 0 || searchFilter.length > 0,
            configurazioni_active_filter: andFilters.length > 0 ? resolvedFilter : undefined,
            configurazioni_page: 0,
          });

          clearSub('configurazioni');
          const sub = _getter
            .subscribe<GetConfigurazioniCerSubscription>({
              query: GetConfigurazioniCerDocument,
              variables: {
                where,
                limit: pageSize,
                offset: 0,
                order_by: store.configurazioni_order_by(),
              } as any,
            })
            .subscribe({
              next: (res) => {
                res?.data?.cer?.map((c) => { _loghi.getLogo(c?.logo?.id); });
                patchState(store, {
                  configurazioni_loading: false,
                  configurazioni_data: res.data?.cer,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  configurazioni_loading: false,
                  configurazioni_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('configurazioni', sub);

          clearSub('configurazioniCount');
          const countSub = _getter
            .subscribe<GetConfigurazioniCerCountSubscription>({
              query: GetConfigurazioniCerCountDocument,
              variables: { where } as any,
            })
            .subscribe({
              next: (res) => {
                patchState(store, { configurazioni_total: res.data?.cer_aggregate?.aggregate?.count ?? 0 });
              },
              error: () => { },
            });
          setSub('configurazioniCount', countSub);

          return sub;
        }),

      changeConfigurazioniPage: (root_id: string, page: number, requestedPageSize?: number) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');
          const _loghi = inject(LoghiStore);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          const pageSize = requestedPageSize ?? store.configurazioni_page_size();
          const offset = page * pageSize;
          const filter = store.configurazioni_active_filter();
          const search = store.configurazioni_search();

          const andFilters: Cer_Bool_Exp[] = [
            ...(filter?.pubblicate ? [{ richieste_pubblicazione: { stato: { _neq: 'inviata' } } } as Cer_Bool_Exp] : []),
            ...(filter?.visibili ? [{ mostra: { _eq: true } } as Cer_Bool_Exp] : []),
            ...(filter?.richieste_abilitate ? [{ abilita_richieste: { _eq: true } } as Cer_Bool_Exp] : []),
          ];
          const searchFilter: Cer_Bool_Exp[] = search?.trim()
            ? [{ alias: { _ilike: `%${search.trim()}%` } }]
            : [];
          const where: Cer_Bool_Exp = {
            root_id: { _eq: root_id },
            ...(andFilters.length > 0 ? { _and: andFilters } : {}),
            ...(searchFilter.length > 0 ? { _or: searchFilter } : {}),
          };

          patchState(store, { configurazioni_page: page, configurazioni_page_size: pageSize });

          clearSub('configurazioni');
          const sub = _getter
            .subscribe<GetConfigurazioniCerSubscription>({
              query: GetConfigurazioniCerDocument,
              variables: { where, limit: pageSize, offset, order_by: store.configurazioni_order_by() } as any,
            })
            .subscribe({
              next: (res) => {
                res?.data?.cer?.map((c) => { _loghi.getLogo(c?.logo?.id); });
                patchState(store, { configurazioni_loading: false, configurazioni_data: res.data?.cer });
              },
              error: (error: any) => {
                patchState(store, { configurazioni_loading: false, configurazioni_data: undefined, failure: error });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('configurazioni', sub);
          return sub;
        }),

      changeConfigurazioniSort: (root_id: string, order_by: Cer_Order_By[]) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');
          const _loghi = inject(LoghiStore);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          const pageSize = store.configurazioni_page_size();
          const filter = store.configurazioni_active_filter();
          const search = store.configurazioni_search();

          const andFilters: Cer_Bool_Exp[] = [
            ...(filter?.pubblicate ? [{ richieste_pubblicazione: { stato: { _neq: 'inviata' } } } as Cer_Bool_Exp] : []),
            ...(filter?.visibili ? [{ mostra: { _eq: true } } as Cer_Bool_Exp] : []),
            ...(filter?.richieste_abilitate ? [{ abilita_richieste: { _eq: true } } as Cer_Bool_Exp] : []),
          ];
          const searchFilter: Cer_Bool_Exp[] = search?.trim()
            ? [{ alias: { _ilike: `%${search.trim()}%` } }]
            : [];
          const where: Cer_Bool_Exp = {
            root_id: { _eq: root_id },
            ...(andFilters.length > 0 ? { _and: andFilters } : {}),
            ...(searchFilter.length > 0 ? { _or: searchFilter } : {}),
          };

          patchState(store, { configurazioni_order_by: order_by, configurazioni_page: 0, configurazioni_loading: true });

          clearSub('configurazioni');
          const sub = _getter
            .subscribe<GetConfigurazioniCerSubscription>({
              query: GetConfigurazioniCerDocument,
              variables: { where, limit: pageSize, offset: 0, order_by } as any,
            })
            .subscribe({
              next: (res) => {
                res?.data?.cer?.map((c) => { _loghi.getLogo(c?.logo?.id); });
                patchState(store, { configurazioni_loading: false, configurazioni_data: res.data?.cer });
              },
              error: (error: any) => {
                patchState(store, { configurazioni_loading: false, configurazioni_data: undefined, failure: error });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('configurazioni', sub);
        }),

      loadSelectedConfigurazione: (id: string) =>
        runInInjectionContext(injector, () => {
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _clear = inject(ClearCerNotificationGQL);

          patchState(store, { selected_configurazione_loading: true });

          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');

          _clear
            .mutate(
              {
                id: id,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .subscribe(() => { });

          clearSub('selectedConfigurazione');

          const sub = _getter
            .subscribe<
              GetConfigurazioneFounderSubscription,
              Exact<{ id?: string }>
            >({
              query: GetConfigurazioneFounderDocument,
              variables: {
                id,
              },
            })
            .subscribe({
              next: (res) => {
                patchState(store, {
                  selected_configurazione: res.data?.cer_by_pk,
                  selected_configurazione_loading: false,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  selected_configurazione: undefined,
                  selected_configurazione_loading: false,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          setSub('selectedConfigurazione', sub);
          return sub;
        }),

      insertOrUpdateConfigurazione: (
        objects: Cer_Insert_Input | Cer_Insert_Input[],
        on_conflict: Cer_On_Conflict,
        cer_geom?: Cer_Geom_Set_Input,
      ) => {
        return runInInjectionContext(injector, () => {
          const _insertOrUpdate = inject(InsertOrUpdateConfigurazioneGQL);
          const _updateCerGeom = inject(UpdateCerGeomGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);

          return _insertOrUpdate
            .mutate(
              {
                objects,
                on_conflict,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              concatMap((insertResult) => {
                const inserted = insertResult.data?.insert_cer?.returning?.[0];

                if (inserted?.id === store.selected_configurazione?.()?.id) {
                  patchState(store, {
                    selected_configurazione: inserted,
                  });
                }

                // Se NON c'è geometria da aggiornare, termina qui
                if (!cer_geom) {
                  return of(insertResult);
                }

                // Altrimenti esegui updateCerGeom DOPO insertOrUpdate
                return _updateCerGeom
                  .mutate(
                    {
                      id: inserted!.id,
                      _set: cer_geom,
                    },
                    {
                      context: {
                        headers: { 'x-hasura-role': 'manager' },
                      },
                    },
                  )
                  .pipe(map(() => insertResult)); // Ritorna comunque l’esito dell’inser
              }),
              tapResponse({
                next: (res) => res, // Se vuoi qualcosa in particolare puoi estrarlo
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },

      clearSelectedConfigurazione: () => {
        runInInjectionContext(injector, async () => {
          clearSub('selectedConfigurazione');
          patchState(store, {
            selected_configurazione_loading: false,
            selected_configurazione: undefined,
          });
        });
      },

      loadSelectedCabina: (
        cabina: NonNullable<
          GetConfigurazioneFounderSubscription['cer_by_pk']
        >['cer_cabina'][0],
      ) => {
        patchState(store, {
          selected_configurazione_selected_cabina_loading: true,
        });
        setTimeout(() => {
          patchState(store, {
            selected_configurazione_selected_cabina_loading: false,
            selected_configurazione_selected_cabina: cabina,
          });
        });
      },
      clearSelectedCabina: () => {
        patchState(store, {
          selected_configurazione_selected_cabina_loading: false,
          selected_configurazione_selected_cabina: undefined,
        });
      },
      updateCodiceGse: (cer_cabina_id: string, codice_gse: string) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(UpdateCodiceGseGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);
          _loading.start();
          return _mutation
            .mutate(
              {
                id: cer_cabina_id,
                codice_gse,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (e) => {
                  _loading.stop();

                  return e.data?.update_cer_cabina_by_pk;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },
      // 🔥 Passaggi di stato della cer_cabina (salva_cr, apri, conferma_apertura, richiedi_modifica, conferma_modifica)
      eliminaCabina: (cer_cabina_id: string): Observable<boolean> => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(EliminaCabinaFounderGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);
          _loading.start();
          return _mutation
            .mutate(
              { id: cer_cabina_id },
              { context: { headers: { 'x-hasura-role': 'manager' } } },
            )
            .pipe(
              map(
                (res) =>
                  !res.errors?.length &&
                  (res.data?.update_cer_cabina?.affected_rows ?? 0) > 0,
              ),
              tap(() => _loading.stop()),
              catchError((error) => {
                patchState(store, { failure: error });
                _loading.stop();
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: error?.graphQLErrors?.[0]?.message ?? error?.message,
                });
                return of(false);
              }),
            );
        });
      },

      cambiaStatoCabina: (
        cer_cabina_id: string,
        action: string,
        codice?: string | null,
      ): Observable<boolean> => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(CambiaStatoCabinaFounderGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);
          _loading.start();
          return _mutation
            .mutate(
              { cercabinaid: cer_cabina_id, action, codice: codice ?? null },
              { context: { headers: { 'x-hasura-role': 'manager' } } },
            )
            .pipe(
              map((res) => !res.errors?.length && !!res.data?.cambia_stato_cabina),
              tap(() => _loading.stop()),
              catchError((error) => {
                patchState(store, { failure: error });
                _loading.stop();
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: error?.graphQLErrors?.[0]?.message ?? error?.message,
                });
                return of(false);
              }),
            );
        });
      },
      // 🔥 Sostituisce lo snapshot vivo (non applicato) della cabina per quel tipo con le pratiche appena esportate
      salvaSnapshotCabina: (
        cer_cabina_id: string,
        tipo: 'consumer' | 'produttore',
        sottoscrizione_ids: string[],
      ): Observable<boolean> => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(SalvaSnapshotCabinaFounderGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return _mutation
            .mutate(
              {
                cer_cabina_id,
                tipo,
                objects: sottoscrizione_ids.map((sottoscrizione_id) => ({
                  cer_cabina_id,
                  sottoscrizione_id,
                  tipo,
                })),
              },
              { context: { headers: { 'x-hasura-role': 'manager' } } },
            )
            .pipe(
              map((res) => !res.errors?.length),
              catchError((error) => {
                patchState(store, { failure: error });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
                return of(false);
              }),
            );
        });
      },
      // 🔥 Collega alla configurazione solo le iniziative nuove; quelle tolte vengono cancellate nella stessa mutation
      setDestinazioniConfigurazione: (cer_id: string, selectedIds: string[]) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(SetDestinazioniConfigurazioneGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);

          const alreadyLinked = new Set(
            (
              store.selected_configurazione?.()
                ?.destinazioni_contributi_collegate ?? []
            ).map((d) => d.destinazione_contributo_id),
          );
          const objects = selectedIds
            .filter((id) => !alreadyLinked.has(id))
            .map((destinazione_contributo_id) => ({
              cer_id,
              destinazione_contributo_id,
            }));

          _loading.start();
          return _mutation
            .mutate(
              { cer_id, keep_ids: selectedIds, objects },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: () => _loading.stop(),
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },
      loadIncentivi: (cer_cabina_id: string) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          patchState(store, {
            selected_configurazione_incentivi_loading: true,
          });
          clearSub('elencoIncentivi');
          const sub = _getter
            .subscribe<GetElencoIncentiviByCerCabinaIdSubscription>({
              query: GetElencoIncentiviByCerCabinaIdDocument,
              variables: {
                cer_cabina_id,
              },
            })
            .subscribe({
              next: (res) => {
                patchState(store, {
                  selected_configurazione_incentivi_loading: false,
                  selected_configurazione_incentivi_data:
                    res.data?.incentivi_ripartizione,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  selected_configurazione_incentivi_loading: false,
                  selected_configurazione_incentivi_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });

          setSub('elencoIncentivi', sub);
          return sub;
        }),
      clearIncentivi: () => {
        clearSub('elencoIncentivi');
        patchState(store, {
          selected_configurazione_incentivi_loading: false,
          selected_configurazione_incentivi_data: undefined,
        });
      },
      insertOrUpdateIncentivo: (
        objects: Incentivi_Ripartizione_Insert_Input,
        on_conflict?: Incentivi_Ripartizione_On_Conflict,
      ) => {
        return runInInjectionContext(injector, () => {
          const _insertOrUpdate = inject(InsertOrUpdateIncentivoGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);
          return _insertOrUpdate
            .mutate(
              {
                objects,
                on_conflict,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (e) => {
                  return e.data?.insert_incentivi_ripartizione;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },
      calcolaRipartizione: (id: string, calcolo: string) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(CalcoloRipartizioneGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);
          _loading.start();
          return _mutation
            .mutate(
              {
                id,
                calcolo,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (e) => {
                  _loading.stop();

                  return e.data?.update_incentivi_ripartizione_by_pk;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },

      mostra: (cerid: string) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(MostraGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return _mutation
            .mutate(
              {
                cerid,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => res,
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },
      abilitaRichieste: (cerid: string) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(AbilitaRichiesteGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return _mutation
            .mutate(
              {
                cerid,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => res,
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },

      setSottoscrizioneFilterData: (data: any) => {
        // TEMP: mappa home founder disattivata, quindi questo bridge
        // (click mappa -> filtro subscriptions) resta spento.
        // Manteniamo il metodo per compatibilita' e rollback rapido.
        // patchState(store, {
        //   sottoscrizioni_filter_data: data,
        // });
      },
      clearCerData: () => {
        // TEMP: mappa home founder disattivata.
        // cabina_data era usata dalla mappa per evidenziare cabine.
        // patchState(store, {
        //   cabina_data: undefined,
        // });
      },
      initSottoscrizioni: (filter?: any, search?: string) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const resolvedSearch =
            search !== undefined ? search : store.sottoscrizioni_search();
          const pageSize = store.sottoscrizioni_page_size();
          const args = sanitizeSottoscrizioniArgs(
            buildSottoscrizioniArgs(filter, resolvedSearch),
          );

          patchState(store, {
            sottoscrizioni_filter: !!(
              filter?.stato?.length ||
              filter?.richiesta?.length ||
              filter?.configurazioni?.length > 0 ||
              filter?.cabina?.length > 0 ||
              filter?.codice_presentatore?.trim() ||
              filter?.codice_personale?.trim() ||
              filter?.data_inserimento_da ||
              filter?.data_inserimento_a ||
              resolvedSearch?.trim()
            ),
            sottoscrizioni_active_filter: filter,
            sottoscrizioni_search: resolvedSearch,
            sottoscrizioni_page: 0,
            sottoscrizioni_loading: true,
          });

          clearSub('sottoscrizioni');
          const sub = _getter
            .subscribe<SearchSottoscrizioniSubscription>({
              query: SearchSottoscrizioniDocument,
              variables: {
                args,
                limit: pageSize,
                offset: 0,
                order_by: store.sottoscrizioni_order_by(),
              } as any,
            })
            .subscribe({
              next: (res) => {
                res?.data?.search_sottoscrizioni?.map((s) => {
                  _loghi.getLogo(s.cer_cabina?.cer?.logo?.id);
                  _loghi.getLogo(s.cer_cabina?.cer?.cer_padre?.logo?.id);
                });
                patchState(store, {
                  sottoscrizioni_loading: false,
                  sottoscrizioni_data: res.data?.search_sottoscrizioni,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  sottoscrizioni_loading: false,
                  sottoscrizioni_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('sottoscrizioni', sub);

          clearSub('sottoscrizioniCount');
          const countSub = _getter
            .subscribe<SearchSottoscrizioniCountSubscription>({
              query: SearchSottoscrizioniCountDocument,
              variables: { args },
            })
            .subscribe({
              next: (res) => {
                patchState(store, {
                  sottoscrizioni_total:
                    res.data?.search_sottoscrizioni_aggregate?.aggregate
                      ?.count ?? 0,
                });
              },
              error: () => { },
            });
          setSub('sottoscrizioniCount', countSub);

          clearSub('sottoscrizioniStati');
          const statiSub = _getter
            .subscribe<SearchSottoscrizioniStatiSubscription>({
              query: SearchSottoscrizioniStatiDocument,
              variables: { args },
            })
            .subscribe({
              next: (res) => {
                const stati = Array.from(
                  new Set(
                    (res.data?.search_sottoscrizioni ?? [])
                      .map((s) => s.stato)
                      .filter(
                        (s): s is Energy_Village__Stato_Sottoscrizione_Enum =>
                          !!s,
                      ),
                  ),
                );
                patchState(store, {
                  sottoscrizioni_stati_data: stati,
                });
              },
              error: () => { },
            });
          setSub('sottoscrizioniStati', statiSub);

          return sub;
        }),

      changePage: (page: number, requestedPageSize?: number) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loghi = inject(LoghiStore);

          const filter = store.sottoscrizioni_active_filter();
          const pageSize =
            requestedPageSize ?? store.sottoscrizioni_page_size();
          const offset = page * pageSize;
          const args = sanitizeSottoscrizioniArgs(
            buildSottoscrizioniArgs(filter, store.sottoscrizioni_search()),
          );

          patchState(store, {
            sottoscrizioni_page: page,
            sottoscrizioni_page_size: pageSize,
          });

          clearSub('sottoscrizioni');
          const sub = _getter
            .subscribe<SearchSottoscrizioniSubscription>({
              query: SearchSottoscrizioniDocument,
              variables: {
                args,
                limit: pageSize,
                offset,
                order_by: store.sottoscrizioni_order_by(),
              } as any,
            })
            .subscribe({
              next: (res) => {
                res?.data?.search_sottoscrizioni?.map((s) => {
                  _loghi.getLogo(s.cer_cabina?.cer?.logo?.id);
                  _loghi.getLogo(s.cer_cabina?.cer?.cer_padre?.logo?.id);
                });
                patchState(store, {
                  sottoscrizioni_loading: false,
                  sottoscrizioni_data: res.data?.search_sottoscrizioni,
                });
              },
              error: (error: any) => {
                patchState(store, {
                  sottoscrizioni_loading: false,
                  sottoscrizioni_data: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('sottoscrizioni', sub);
          return sub;
        }),

      changeSort: (order_by: Sottoscrizione_Order_By[]) =>
        runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');
          const _loghi = inject(LoghiStore);

          const filter = store.sottoscrizioni_active_filter();
          const pageSize = store.sottoscrizioni_page_size();
          const args = sanitizeSottoscrizioniArgs(
            buildSottoscrizioniArgs(filter, store.sottoscrizioni_search()),
          );

          patchState(store, {
            sottoscrizioni_order_by: order_by,
            sottoscrizioni_page: 0,
            sottoscrizioni_loading: true,
          });

          clearSub('sottoscrizioni');
          const sub = _getter
            .subscribe<SearchSottoscrizioniSubscription>({
              query: SearchSottoscrizioniDocument,
              variables: { args, limit: pageSize, offset: 0, order_by } as any,
            })
            .subscribe({
              next: (res) => {
                res?.data?.search_sottoscrizioni?.map((s) => {
                  _loghi.getLogo(s.cer_cabina?.cer?.logo?.id);
                  _loghi.getLogo(s.cer_cabina?.cer?.cer_padre?.logo?.id);
                });
                patchState(store, {
                  sottoscrizioni_loading: false,
                  sottoscrizioni_data: res.data?.search_sottoscrizioni,
                });
              },
              error: () => {
                patchState(store, {
                  sottoscrizioni_loading: false,
                });
              },
            });
          setSub('sottoscrizioni', sub);
        }),

      loadSelectedSottoscrizione: (id: number) => {
        return runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient('manager');
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          patchState(store, { selected_sottoscrizione_loading: true });
          clearSub('selectedSottoscrizione');
          const sub = _getter
            .subscribe<GetSottoscrizioneFounderSubscription>({
              query: GetSottoscrizioneFounderDocument,
              variables: {
                id,
              },
            })
            .subscribe({
              next: (res) => {
                patchState(store, {
                  selected_sottoscrizione_loading: false,
                  selected_sottoscrizione: res.data?.sottoscrizione_by_pk,
                });
              },
              error: (error) => {
                patchState(store, {
                  selected_sottoscrizione_loading: false,
                  selected_sottoscrizione: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
              },
            });
          setSub('selectedSottoscrizione', sub);
          return sub;
        });
      },
      clearSelectedSottoscrizione: () => {
        clearSub('selectedSottoscrizione');
        patchState(store, {
          selected_sottoscrizione_loading: false,
          selected_sottoscrizione: undefined,
          configurazioni_loading: false,
        });
      },
      cambiaStatoSottoscrizione: (
        args: InputMaybe<Cambia_Stato_Founder_Args>,
      ) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(CambiaStatoFounderGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          patchState(store, { selected_sottoscrizione_loading: true });

          return _mutation
            .mutate(
              {
                args,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  return res;
                },
                error: (error) => {
                  patchState(store, {
                    failure: error,
                    selected_sottoscrizione_loading: false,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', {
                      error,
                    }),
                  });
                },
              }),
            );
        });
      },
      insertOrUpdateSottoscrizione: (
        objects: Sottoscrizione_Insert_Input,
        on_conflict: Sottoscrizione_On_Conflict,
      ) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(InsertOrUpdateSottoscrizioneGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          const sanitizeIndirizzoData = (value: any): any => {
            if (!value || typeof value !== 'object') return value;
            const next: any = Array.isArray(value) ? [] : {};
            for (const [key, raw] of Object.entries(value)) {
              if (key === '__typename') continue;
              if (
                key === 'citta' ||
                key === 'comune' ||
                key === 'city' ||
                key === 'locality' ||
                key === 'geom'
              ) {
                continue;
              }
              if (Array.isArray(raw)) {
                next[key] = raw.map((entry) => sanitizeIndirizzoData(entry));
              } else if (raw && typeof raw === 'object') {
                next[key] = sanitizeIndirizzoData(raw);
              } else {
                next[key] = raw;
              }
            }
            return next;
          };

          const sanitizeMutationObject = (
            value: any,
            parentKey?: string,
          ): any => {
            if (Array.isArray(value)) {
              return value.map((entry) =>
                sanitizeMutationObject(entry, parentKey),
              );
            }
            if (value instanceof Date) return value;
            if (!value || typeof value !== 'object') return value;
            const next: any = {};
            for (const [key, raw] of Object.entries(value)) {
              if (key === '__typename') continue;
              if (parentKey === 'indirizzo' && key === 'data') {
                next[key] = sanitizeIndirizzoData(raw);
              } else {
                next[key] = sanitizeMutationObject(raw, key);
              }
            }
            return next;
          };

          const sanitizedObjects = sanitizeMutationObject(
            objects,
          ) as Sottoscrizione_Insert_Input;

          return _mutation
            .mutate(
              {
                objects: sanitizedObjects,
                on_conflict,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tap({
                error: (error: any) => {
                  patchState(store, { failure: error });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },
      savePersonaIndirizzo: (personaId: string, addressMap: any) =>
        runInInjectionContext(injector, () => {
          const _insertOrUpdate = inject(InsertOrUpdatePersonaFounderGQL);

          const _loading = inject(LoadingService);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          const completo = addressMap?.completo ?? '';
          const coordinates = Array.isArray(addressMap?.geom_?.coordinates)
            ? [...addressMap.geom_.coordinates]
            : null;

          if (!completo) {
            _loading.stop();
            _toast.add({
              key: 'global-toast',
              severity: 'error',
              summary: _translate.instant('toast.error.title'),
              detail: _translate.instant('formly.validation.required'),
            });
            return EMPTY;
          }

          const mutationContext = {
            context: {
              headers: { 'x-hasura-role': 'manager' },
            },
          };

          console.log(addressMap);

          return _insertOrUpdate
            .mutate(
              {
                objects: {
                  id: personaId,
                  indirizzo: {
                    on_conflict: {
                      constraint: Indirizzo_Constraint.IndirizzoPkey,
                      update_columns: [
                        Indirizzo_Update_Column.Completo,
                        Indirizzo_Update_Column.Toponimo,
                        Indirizzo_Update_Column.Civico,
                        Indirizzo_Update_Column.Cap,
                        Indirizzo_Update_Column.Geom,
                        Indirizzo_Update_Column.Manuale,
                      ],
                    },
                    data: {
                      completo,
                      toponimo: addressMap?.toponimo ?? null,
                      civico: addressMap?.civico ?? null,
                      cap: addressMap?.cap ?? null,
                      id: addressMap?.id ?? undefined, // se c'è l'id lo usiamo per update, altrimenti lo lasciamo undefined per insert
                      geom_:
                        Array.isArray(coordinates) && coordinates.length === 2
                          ? {
                            type: 'Point',
                            coordinates,
                          }
                          : null,
                      manuale: addressMap?.manuale ?? false,
                    },
                  },
                },
                on_conflict: {
                  constraint: Persona_Constraint.PersonaPkey,
                  update_columns: [Persona_Update_Column.IndirizzoId],
                },
              },
              mutationContext,
            )
            .pipe(
              map(
                (response) =>
                  response.data?.insert_persona?.returning?.[0]?.indirizzo ??
                  null,
              ),
              tap(() => _loading.stop()),
              catchError((error) => {
                _loading.stop();
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
                return EMPTY;
              }),
            );
        }),
      updatePersona: (id: string, _set: Persona_Set_Input) =>
        runInInjectionContext(injector, () => {
          const _loading = inject(LoadingService);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return inject(UpdatePersonaFounderGQL)
            .mutate(
              { id, _set },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tap(() => _loading.stop()),
              catchError((error) => {
                _loading.stop();
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
                throw error;
              }),
            );
        }),

      updateContatto: (id: string, _set: Contatto_Set_Input) =>
        runInInjectionContext(injector, () => {
          const _loading = inject(LoadingService);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return inject(UpdateContattoFounderGQL)
            .mutate(
              { id, _set },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tap(() => _loading.stop()),
              catchError((error) => {
                _loading.stop();
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
                throw error;
              }),
            );
        }),

      updateAsset: (id: string, _set: Asset_Set_Input) =>
        runInInjectionContext(injector, () => {
          const _loading = inject(LoadingService);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return inject(UpdateAssetFounderGQL)
            .mutate(
              { id, _set },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tap(() => _loading.stop()),
              catchError((error) => {
                _loading.stop();
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
                throw error;
              }),
            );
        }),

      updateAssetProduzione: (id: string, _set: Asset_Produzione_Set_Input) =>
        runInInjectionContext(injector, () => {
          const _loading = inject(LoadingService);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return inject(UpdateAssetProduzioneFounderGQL)
            .mutate(
              { id, _set },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tap(() => _loading.stop()),
              catchError((error) => {
                _loading.stop();
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
                throw error;
              }),
            );
        }),

      updateAssetProduzioneUnita: (
        id: string,
        _set: Asset_Produzione_Unita_Set_Input,
      ) =>
        runInInjectionContext(injector, () => {
          const _loading = inject(LoadingService);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return inject(UpdateAssetProduzioneUnitaFounderGQL)
            .mutate(
              { id, _set },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tap(() => _loading.stop()),
              catchError((error) => {
                _loading.stop();
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
                throw error;
              }),
            );
        }),

      updateAssetConsumo: (id: string, _set: Asset_Consumo_Set_Input) =>
        runInInjectionContext(injector, () => {
          const _loading = inject(LoadingService);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return inject(UpdateAssetConsumoFounderGQL)
            .mutate(
              { id, _set },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tap(() => _loading.stop()),
              catchError((error) => {
                _loading.stop();
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
                throw error;
              }),
            );
        }),

      updateCondivisione: (
        id: string,
        _set: Condivisione_Condivisione_Set_Input,
      ) =>
        runInInjectionContext(injector, () => {
          const _loading = inject(LoadingService);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return inject(UpdateCondivisioneFounderGQL)
            .mutate(
              { id, _set },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tap(() => _loading.stop()),
              catchError((error) => {
                _loading.stop();
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
                throw error;
              }),
            );
        }),

      initListaUtenti: () =>
        runInInjectionContext(injector, () => {
          const _getter = inject(GetListaUtentiFounderGQL);
          const _counter = inject(GetListaUtentiFounderCountGQL);
          const ROLE = {
            context: { headers: { 'x-hasura-role': 'geomarketing' } },
          };
          const limit = store.lista_utenti_page_size();
          const order_by = store.lista_utenti_order_by();
          const args = {
            p_search: store.lista_utenti_search()?.trim() || null,
          };
          const where = buildListaUtentiWhere(
            store.lista_utenti_active_filter(),
          );
          patchState(store, {
            lista_utenti_loading: true,
            lista_utenti_page: 0,
          });

          _getter
            .fetch({ args, where, limit, offset: 0, order_by } as any, ROLE)
            .pipe(
              tapResponse({
                next: (res) => {
                  patchState(store, {
                    lista_utenti_loading: false,
                    lista_utenti_data: res.data?.search_user_entity,
                  });
                },
                error: () => {
                  patchState(store, { lista_utenti_loading: false });
                },
              }),
            )
            .subscribe();

          _counter
            .fetch({ args, where }, ROLE)
            .pipe(
              tapResponse({
                next: (res) => {
                  patchState(store, {
                    lista_utenti_total:
                      res.data?.search_user_entity_aggregate?.aggregate
                        ?.count ?? 0,
                  });
                },
                error: () => { },
              }),
            )
            .subscribe();
        }),

      initListaUtentiWhitelabels: () =>
        runInInjectionContext(injector, () => {
          const _getter = inject(GetWhiteLabelsListaUtentiFounderGQL);
          const ROLE = { context: { headers: { 'x-hasura-role': 'manager' } } };
          patchState(store, { lista_utenti_whitelabels_loading: true });
          _getter
            .fetch({}, ROLE)
            .pipe(
              tapResponse({
                next: (res) => {
                  const seen = new Set<string>();
                  const whitelabels = (res.data?.cer ?? [])
                    .flatMap((c: any) => c.whitelabel ?? [])
                    .map((wl: any) => wl.configurazione?.referral)
                    .filter(
                      (r: any): r is string =>
                        !!r && !seen.has(r) && seen.add(r) !== undefined,
                    )
                    .sort()
                    .map((referral: string) => ({ referral }));
                  patchState(store, {
                    lista_utenti_whitelabels_loading: false,
                    lista_utenti_whitelabels_data: whitelabels,
                  });
                },
                error: () => {
                  patchState(store, {
                    lista_utenti_whitelabels_loading: false,
                  });
                },
              }),
            )
            .subscribe();
        }),

      changePageListaUtenti: (page: number, requestedPageSize?: number) =>
        runInInjectionContext(injector, () => {
          const _getter = inject(GetListaUtentiFounderGQL);
          const ROLE = {
            context: { headers: { 'x-hasura-role': 'geomarketing' } },
          };
          const pageSize = requestedPageSize ?? store.lista_utenti_page_size();
          const order_by = store.lista_utenti_order_by();
          const args = {
            p_search: store.lista_utenti_search()?.trim() || null,
          };
          const where = buildListaUtentiWhere(
            store.lista_utenti_active_filter(),
          );
          const offset = page * pageSize;
          patchState(store, {
            lista_utenti_loading: true,
            lista_utenti_page: page,
            lista_utenti_page_size: pageSize,
          });
          _getter
            .fetch(
              { args, where, limit: pageSize, offset, order_by } as any,
              ROLE,
            )
            .pipe(
              tapResponse({
                next: (res) => {
                  patchState(store, {
                    lista_utenti_loading: false,
                    lista_utenti_data: res.data?.search_user_entity,
                  });
                },
                error: () => {
                  patchState(store, { lista_utenti_loading: false });
                },
              }),
            )
            .subscribe();
        }),

      getAllListaUtenti: () =>
        runInInjectionContext(injector, () => {
          const _getter = inject(GetListaUtentiFounderGQL);
          const ROLE = {
            context: { headers: { 'x-hasura-role': 'geomarketing' } },
          };
          const order_by = store.lista_utenti_order_by();
          const limit = store.lista_utenti_total() || 10000;
          const args = {
            p_search: store.lista_utenti_search()?.trim() || null,
          };
          const where = buildListaUtentiWhere(
            store.lista_utenti_active_filter(),
          );
          return _getter.fetch(
            { args, where, limit, offset: 0, order_by } as any,
            ROLE,
          );
        }),

      changeSortListaUtenti: (order_by: Auth_User_Entity_Order_By[]) =>
        runInInjectionContext(injector, () => {
          const _getter = inject(GetListaUtentiFounderGQL);
          const ROLE = {
            context: { headers: { 'x-hasura-role': 'geomarketing' } },
          };
          const limit = store.lista_utenti_page_size();
          const args = {
            p_search: store.lista_utenti_search()?.trim() || null,
          };
          const where = buildListaUtentiWhere(
            store.lista_utenti_active_filter(),
          );
          patchState(store, {
            lista_utenti_order_by: order_by,
            lista_utenti_loading: true,
            lista_utenti_page: 0,
          });
          _getter
            .fetch({ args, where, limit, offset: 0, order_by } as any, ROLE)
            .pipe(
              tapResponse({
                next: (res) => {
                  patchState(store, {
                    lista_utenti_loading: false,
                    lista_utenti_data: res.data?.search_user_entity,
                  });
                },
                error: () => {
                  patchState(store, { lista_utenti_loading: false });
                },
              }),
            )
            .subscribe();
        }),
      changeSearchListaUtenti: (search: string) =>
        runInInjectionContext(injector, () => {
          const _getter = inject(GetListaUtentiFounderGQL);
          const _counter = inject(GetListaUtentiFounderCountGQL);
          const ROLE = {
            context: { headers: { 'x-hasura-role': 'geomarketing' } },
          };
          const limit = store.lista_utenti_page_size();
          const order_by = store.lista_utenti_order_by();
          const trimmed = search?.trim?.() ?? '';
          const args = { p_search: trimmed.length > 0 ? trimmed : null };
          const where = buildListaUtentiWhere(
            store.lista_utenti_active_filter(),
          );

          patchState(store, {
            lista_utenti_search: trimmed,
            lista_utenti_loading: true,
            lista_utenti_page: 0,
          });

          _getter
            .fetch({ args, where, limit, offset: 0, order_by } as any, ROLE)
            .pipe(
              tapResponse({
                next: (res) => {
                  patchState(store, {
                    lista_utenti_loading: false,
                    lista_utenti_data: res.data?.search_user_entity,
                  });
                },
                error: () => {
                  patchState(store, { lista_utenti_loading: false });
                },
              }),
            )
            .subscribe();

          _counter
            .fetch({ args, where }, ROLE)
            .pipe(
              tapResponse({
                next: (res) => {
                  patchState(store, {
                    lista_utenti_total:
                      res.data?.search_user_entity_aggregate?.aggregate
                        ?.count ?? 0,
                  });
                },
                error: () => { },
              }),
            )
            .subscribe();
        }),
      applyFilterListaUtenti: (filter: {
        whitelabel?: string[];
        tipologia_profilo?: string[];
      }) =>
        runInInjectionContext(injector, () => {
          const _getter = inject(GetListaUtentiFounderGQL);
          const _counter = inject(GetListaUtentiFounderCountGQL);
          const ROLE = {
            context: { headers: { 'x-hasura-role': 'geomarketing' } },
          };
          const limit = store.lista_utenti_page_size();
          const order_by = store.lista_utenti_order_by();
          const args = {
            p_search: store.lista_utenti_search()?.trim() || null,
          };
          const where = buildListaUtentiWhere(filter ?? {});

          patchState(store, {
            lista_utenti_active_filter: filter ?? {},
            lista_utenti_loading: true,
            lista_utenti_page: 0,
          });

          _getter
            .fetch({ args, where, limit, offset: 0, order_by } as any, ROLE)
            .pipe(
              tapResponse({
                next: (res) => {
                  patchState(store, {
                    lista_utenti_loading: false,
                    lista_utenti_data: res.data?.search_user_entity,
                  });
                },
                error: () => {
                  patchState(store, { lista_utenti_loading: false });
                },
              }),
            )
            .subscribe();

          _counter
            .fetch({ args, where }, ROLE)
            .pipe(
              tapResponse({
                next: (res) => {
                  patchState(store, {
                    lista_utenti_total:
                      res.data?.search_user_entity_aggregate?.aggregate
                        ?.count ?? 0,
                  });
                },
                error: () => { },
              }),
            )
            .subscribe();
        }),

      clear: () => {
        clearAllSubs();
        patchState(store, initialFounderState);
      },

      getElencoDatiProduttore: () => {
        return runInInjectionContext(injector, () => {
          const _getter = inject(GetElencoDatiProduttoreFounderGQL);

          return _getter.fetch(
            {
              where: buildSottoscrizioniWhere(
                store.sottoscrizioni_active_filter(),
                store.sottoscrizioni_search(),
              ),
            },
            {
              context: {
                headers: { 'x-hasura-role': 'manager' },
              },
            },
          );
        });
      },

      /**
       * `filterOverride` lets a caller export by a *different* filter than
       * whatever's live on the page (e.g. a preset's own saved filter)
       * without touching `sottoscrizioni_active_filter`/`_search` — the page
       * listing and its badge stay exactly as they were. Omit it (as every
       * pre-existing call site still does) to keep today's behavior:
       * export by the page's current filter.
       */
      getExportElencoRichieste: (
        variables?: Record<string, boolean>,
        filterOverride?: { model?: any; search?: string },
      ) => {
        return runInInjectionContext(injector, () => {
          const _getter = inject(GetExportElencoRichiesteFounderGQL);

          return _getter.fetch(
            {
              where: buildSottoscrizioniWhere(
                filterOverride?.model ?? store.sottoscrizioni_active_filter(),
                filterOverride?.search ?? store.sottoscrizioni_search(),
              ),
              ...variables,
            },
            {
              context: {
                headers: { 'x-hasura-role': 'manager' },
              },
            },
          );
        });
      },

      getExportCustom: () => {
        return runInInjectionContext(injector, () => {
          const _getter = inject(GetExportCustomFounderGQL);

          return _getter.fetch(
            {
              where: buildSottoscrizioniWhere(
                store.sottoscrizioni_active_filter(),
                store.sottoscrizioni_search(),
              ),
            },
            {
              context: {
                headers: { 'x-hasura-role': 'manager' },
              },
            },
          );
        });
      },

      getConfigurazioneId: (referral: string) => {
        return runInInjectionContext(injector, () => {
          const _getter = inject(GetConfigurazioneIdFounderGQL);

          return _getter.fetch(
            { referral },
            {
              context: {
                headers: { 'x-hasura-role': 'manager' },
              },
            },
          );
        });
      },

      getPreset: (where?: Preset_Bool_Exp) => {
        return runInInjectionContext(injector, () => {
          const _getter = inject(GetPresetFounderGQL);

          return _getter.fetch(
            { where },
            {
              context: {
                headers: { 'x-hasura-role': 'manager' },
              },
            },
          );
        });
      },

      insertOrUpdatePreset: (
        object: Preset_Insert_Input,
        on_conflict: Preset_On_Conflict,
      ) => {
        return runInInjectionContext(injector, () => {
          const _insertOrUpdate = inject(InsertOrUpdatePresetFounderGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);
          return _insertOrUpdate
            .mutate(
              { object, on_conflict },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (e) => e.data?.insert_preset_one,
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },

      deletePreset: (id: string) => {
        return runInInjectionContext(injector, () => {
          const _delete = inject(DeletePresetFounderGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _loading = inject(LoadingService);
          return _delete
            .mutate(
              { id },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: (e) => e.data?.delete_preset_by_pk,
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _loading.stop();
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },

      // 🔥 Con "ids" si esportano solo le pratiche scelte dal gestore (usato per le cabine)
      getExportFlussoConsumatori2026: (
        cerCabinaId?: string,
        ids?: string[],
      ) => {
        return runInInjectionContext(injector, () => {
          const _getter = inject(GetExportFlussoConsumatori2026FounderGQL);

          return _getter.fetch(
            {
              where: {
                ...(ids ? { id: { _in: ids } } : {}),
                // 🔥 Per una cabina si esportano solo le pratiche pronte per il GSE di quella cabina, senza i filtri della lista
                ...(cerCabinaId
                  ? { cer_cabina_id: { _eq: cerCabinaId } }
                  : buildSottoscrizioniWhere(
                  store.sottoscrizioni_active_filter(),
                  store.sottoscrizioni_search(),
                  )),
                tipologia_richiesta: {
                  _eq: _Sottoscrizione_Tipo_Richiesta_Enum.Consumer,
                },
                stato: {
                  _in: cerCabinaId
                    ? [Energy_Village__Stato_Sottoscrizione_Enum.ProntoPerGse]
                    : [
                    Energy_Village__Stato_Sottoscrizione_Enum.ProntoPerGse,
                    Energy_Village__Stato_Sottoscrizione_Enum.InviatoAlGse,
                    Energy_Village__Stato_Sottoscrizione_Enum.Associata,
                    Energy_Village__Stato_Sottoscrizione_Enum.DaVerificare,
                  ],
                },
              },
            },
            {
              context: {
                headers: { 'x-hasura-role': 'manager' },
              },
            },
          );
        });
      },

      getExportFlussoProduttoriGSE: (cerCabinaId?: string, ids?: string[]) => {
        return runInInjectionContext(injector, () => {
          const _getter = inject(GetExportFlussoProduttoriGseFounderGQL);

          return _getter.fetch(
            {
              where: {
                ...(ids ? { id: { _in: ids } } : {}),
                // 🔥 Per una cabina si esportano solo le pratiche pronte per il GSE di quella cabina, senza i filtri della lista
                ...(cerCabinaId
                  ? { cer_cabina_id: { _eq: cerCabinaId } }
                  : buildSottoscrizioniWhere(
                  store.sottoscrizioni_active_filter(),
                  store.sottoscrizioni_search(),
                  )),
                tipologia_richiesta: {
                  _in: [
                    _Sottoscrizione_Tipo_Richiesta_Enum.Prosumer,
                    _Sottoscrizione_Tipo_Richiesta_Enum.Future,
                  ],
                },
                stato: {
                  _in: cerCabinaId
                    ? [Energy_Village__Stato_Sottoscrizione_Enum.ProntoPerGse]
                    : [
                    Energy_Village__Stato_Sottoscrizione_Enum.ProntoPerGse,
                    Energy_Village__Stato_Sottoscrizione_Enum.InviatoAlGse,
                    Energy_Village__Stato_Sottoscrizione_Enum.Associata,
                    Energy_Village__Stato_Sottoscrizione_Enum.DaVerificare,
                  ],
                },
              },
            },
            {
              context: {
                headers: { 'x-hasura-role': 'manager' },
              },
            },
          );
        });
      },

      // insertOrUpdateStagingSottoscrizione: (
      //   objects: Staging_Gestore_Sottoscrizione_Insert_Input,
      //   on_conflict: Staging_Gestore_Sottoscrizione_On_Conflict,
      // ) => {
      //   return runInInjectionContext(injector, () => {
      //     const _insertOrUpdate = inject(
      //       InsertOrUpdateStagingSottoscrizioneGQL,
      //     );
      //     const _toast = inject(MessageService);
      //     const _translate = inject(TranslateService);
      //     const _userStore = inject(UserStore);
      //     const _loading = inject(LoadingService);
      //     _loading.start();
      //     return _insertOrUpdate
      //       .mutate(
      //         {
      //           objects: { ...objects, auth_id: _userStore.user_data?.()?.sub },
      //           on_conflict,
      //         },
      //         {
      //           context: {
      //             headers: { 'x-hasura-role': 'manager' },
      //           },
      //         },
      //       )
      //       .pipe(
      //         tapResponse({
      //           next: (res) => {
      //             _loading.stop();
      //             return res;
      //           },
      //           error: (error) => {
      //             patchState(store, {
      //               failure: error,
      //             });
      //             _loading.stop();
      //             _toast.add({
      //               key: 'global-toast',
      //               severity: 'error',
      //               summary: _translate.instant('toast.error.title'),
      //               detail: _translate.instant('toast.error.detail', {
      //                 error,
      //               }),
      //             });
      //           },
      //         }),
      //       );
      //   });
      // },
      // initStagingSottoscrizioni: () => {
      //   const _getter = inject(GetStagingSottoscrizioniGQL);
      //   const _toast = inject(MessageService);
      //   const _translate = inject(TranslateService);

      //   return runInInjectionContext(injector, () => {
      //     return _getter.fetch(
      //       { where: {} },
      //       {
      //         context: {
      //           headers: { 'x-hasura-role': 'manager' },
      //         },
      //       },
      //     );
      //   }).subscribe({
      //     next: (res) => {
      //       patchState(store, {
      //         staging_sottoscrizione_loading: false,
      //         staging_sottoscrizione_data: res.data?.staging_sottoscrizione,
      //       });
      //     },
      //     error: (error) => {
      //       patchState(store, {
      //         staging_sottoscrizione_loading: false,
      //         staging_sottoscrizione_data: undefined,
      //         failure: error,
      //       });
      //       _toast.add({
      //         key: 'global-toast',
      //         severity: 'error',
      //         summary: _translate.instant(`toast.error.title`),
      //         detail: _translate.instant(`toast.error.detail`, {
      //           error,
      //         }),
      //       });
      //     },
      //   });
      // },
      // loadSelectedStagingSottoscrizione: (id: string) => {
      //   const _getter = inject(GetStagingSottoscrizioneFounderGQL);
      //   const _toast = inject(MessageService);
      //   const _translate = inject(TranslateService);

      //   return runInInjectionContext(injector, () => {
      //     return _getter.fetch({
      //       id: {
      //         _eq: id,
      //       },
      //     });
      //   }).subscribe({
      //     next: (res) => {
      //       patchState(store, {
      //         staging_sottoscrizione_loading: false,
      //         selected_staging_sottoscrizione:
      //           res.data?.staging_sottoscrizione_by_pk,
      //       });
      //     },
      //     error: (error) => {
      //       patchState(store, {
      //         staging_sottoscrizione_loading: false,
      //         selected_staging_sottoscrizione: undefined,
      //         failure: error,
      //       });
      //       _toast.add({
      //         key: 'global-toast',
      //         severity: 'error',
      //         summary: _translate.instant(`toast.error.title`),
      //         detail: _translate.instant(`toast.error.detail`, {
      //           error,
      //         }),
      //       });
      //     },
      //   });
      // },
      // patchSelectedStagingSottocrizione: (
      //   sottoscrizione: GetStagingSottoscrizioneFounderQuery['staging_sottoscrizione_by_pk'],
      // ) => {
      //   patchState(store, {
      //     selected_staging_sottoscrizione: sottoscrizione,
      //   });
      // },
      // patchSelectedLotto: (
      //   lotto: DeepPartial<GetStagingLottiQuery['staging_lotto'][0]>,
      // ) => {
      //   patchState(store, {
      //     selected_lotto: lotto,
      //   });
      // },
      // initStagingLotti() {
      //   const _getter = inject(GetStagingLottiGQL);
      //   const _toast = inject(MessageService);
      //   const _translate = inject(TranslateService);

      //   return runInInjectionContext(injector, () => {
      //     return _getter.fetch(
      //       {},
      //       {
      //         context: {
      //           headers: { 'x-hasura-role': 'manager' },
      //         },
      //       },
      //     );
      //   }).subscribe({
      //     next: (res) => {
      //       patchState(store, {
      //         staging_lotti_data: res.data?.staging_lotto,
      //         staging_lotti_loading: false,
      //       });
      //     },
      //     error: (error) => {
      //       patchState(store, {
      //         staging_lotti_data: undefined,
      //         staging_lotti_loading: false,
      //         failure: error,
      //       });
      //       _toast.add({
      //         key: 'global-toast',
      //         severity: 'error',
      //         summary: _translate.instant(`toast.error.title`),
      //         detail: _translate.instant(`toast.error.detail`, {
      //           error,
      //         }),
      //       });
      //     },
      //   });
      // },
      // insertOrUpdateStagingLotto: (
      //   objects: Staging_Gestore_Lotto_Insert_Input,
      //   on_conflict?: Staging_Gestore_Lotto_On_Conflict,
      // ) => {
      //   return runInInjectionContext(injector, () => {
      //     const _insertOrUpdate = inject(InsertOrUpdateStagingLottoGQL);
      //     const _toast = inject(MessageService);
      //     const _translate = inject(TranslateService);
      //     const _userStore = inject(UserStore);
      //     const _loading = inject(LoadingService);

      //     return _insertOrUpdate
      //       .mutate(
      //         {
      //           objects: { ...objects, auth_id: _userStore.user_data?.()?.sub },
      //           on_conflict,
      //         },
      //         {
      //           context: {
      //             headers: { 'x-hasura-role': 'manager' },
      //           },
      //         },
      //       )
      //       .pipe(
      //         tapResponse({
      //           next: (res) => res,
      //           error: (error) => {
      //             patchState(store, {
      //               failure: error,
      //             });
      //             _toast.add({
      //               key: 'global-toast',
      //               severity: 'error',
      //               summary: _translate.instant('toast.error.title'),
      //               detail: _translate.instant('toast.error.detail', {
      //                 error,
      //               }),
      //             });
      //           },
      //         }),
      //       );
      //   });
      // },
      // updateStatoStagingSottoscrizioni: (
      //   ids: string[],
      //   stato: Staging__Stato_Sottoscrizione_Enum,
      // ) => {
      //   return runInInjectionContext(injector, () => {
      //     const _mutation = inject(UpdateStatoStagingSottoscrizioniGQL);
      //     const _toast = inject(MessageService);
      //     const _translate = inject(TranslateService);
      //     const _loading = inject(LoadingService);

      //     _loading.start();

      //     return _mutation
      //       .mutate(
      //         {
      //           ids,
      //           stato,
      //         },
      //         {
      //           context: {
      //             headers: { 'x-hasura-role': 'manager' },
      //           },
      //         },
      //       )
      //       .pipe(
      //         tapResponse({
      //           next: (res) => {
      //             _loading.stop();
      //             _toast.add({
      //               key: 'global-toast',
      //               severity: 'success',
      //               summary: _translate.instant('toast.success.title'),
      //               detail: _translate.instant('toast.success.detail', {
      //                 count:
      //                   res.data?.update_staging_sottoscrizione
      //                     ?.affected_rows || 0,
      //               }),
      //             });
      //             return res;
      //           },
      //           error: (error) => {
      //             patchState(store, {
      //               failure: error,
      //             });
      //             _loading.stop();
      //             _toast.add({
      //               key: 'global-toast',
      //               severity: 'error',
      //               summary: _translate.instant('toast.error.title'),
      //               detail: _translate.instant('toast.error.detail', {
      //                 error,
      //               }),
      //             });
      //           },
      //         }),
      //       );
      //   });
      // },
      sendEmail: (destinatario: string, oggetto: string, message: string) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(InsertEmailGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return _mutation
            .mutate(
              { destinatario, oggetto, message },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: () => {
                  _toast.add({
                    key: 'global-toast',
                    severity: 'success',
                    summary: _translate.instant('sottoscrizione.invio.email_dialog.inviata'),
                  });
                },
                error: (error) => {
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },

      sendEmailMultiple: (
        destinatari: string[],
        oggetto: string,
        message: string,
      ): Observable<{ inviate: number; fallite: number }> => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(InsertEmailGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          // Invii sequenziali: un errore su un destinatario non blocca gli altri
          return from(destinatari).pipe(
            concatMap((destinatario) =>
              _mutation
                .mutate(
                  { destinatario, oggetto, message },
                  { context: { headers: { 'x-hasura-role': 'manager' } } },
                )
                .pipe(
                  map(() => true),
                  catchError(() => of(false)),
                ),
            ),
            toArray(),
            map((esiti) => {
              const inviate = esiti.filter(Boolean).length;
              return { inviate, fallite: esiti.length - inviate };
            }),
            tap(({ inviate, fallite }) => {
              _toast.add({
                key: 'global-toast',
                severity: fallite > 0 ? 'warn' : 'success',
                summary: _translate.instant(
                  'sottoscrizione.selezione.email_inviate',
                  { inviate, fallite },
                ),
              });
            }),
          );
        });
      },

      // Modelli email (comunicazioni.email_template): condivisi per whitelabel oppure personali
      getEmailTemplates: (): Observable<
        GetEmailTemplatesQuery['comunicazioni_email_template']
      > => {
        return emailTemplateScope().pipe(
          switchMap(({ configurazione_id, auth_id }) => {
            // 🔥 Hasura controlla solo l'autorizzazione: la whitelabel corrente va ristretta qui
            const or = [
              ...(configurazione_id
                ? [{ configurazione_id: { _eq: configurazione_id } }]
                : []),
              ...(auth_id ? [{ auth_id: { _eq: auth_id } }] : []),
            ];
            if (!or.length) return of([]);
            return runInInjectionContext(injector, () =>
              inject(GetEmailTemplatesGQL)
                .fetch(
                  { where: { _or: or } },
                  {
                    fetchPolicy: 'network-only',
                    context: { headers: { 'x-hasura-role': 'manager' } },
                  },
                )
                .pipe(
                  map((res) => res.data?.comunicazioni_email_template ?? []),
                  catchError(() => of([])),
                ),
            );
          }),
        );
      },

      saveEmailTemplate: (
        nome: string,
        oggetto: string,
        message: string,
      ): Observable<boolean> => {
        return emailTemplateScope().pipe(
          switchMap(({ configurazione_id, auth_id }) =>
            runInInjectionContext(injector, () => {
              const _mutation = inject(InsertEmailTemplateGQL);
              const _toast = inject(MessageService);
              const _translate = inject(TranslateService);
              // whitelabel -> condiviso (configurazione_id), altrimenti personale (auth_id)
              const scope = configurazione_id
                ? { configurazione_id }
                : auth_id
                  ? { auth_id }
                  : null;
              if (!scope) return of(false);
              return _mutation
                .mutate(
                  { object: { ...scope, nome, oggetto, message } },
                  { context: { headers: { 'x-hasura-role': 'manager' } } },
                )
                .pipe(
                  map(() => true),
                  tap(() =>
                    _toast.add({
                      key: 'global-toast',
                      severity: 'success',
                      summary: _translate.instant(
                        'sottoscrizione.modelli.salvato',
                      ),
                    }),
                  ),
                  catchError((error) => {
                    _toast.add({
                      key: 'global-toast',
                      severity: 'error',
                      summary: _translate.instant('toast.error.title'),
                      detail: _translate.instant('toast.error.detail', {
                        error,
                      }),
                    });
                    return of(false);
                  }),
                );
            }),
          ),
        );
      },

      updateEmailTemplate: (
        id: string,
        nome: string,
        oggetto: string,
        message: string,
      ): Observable<boolean> => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(UpdateEmailTemplateGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return _mutation
            .mutate(
              { id, nome, oggetto, message },
              { context: { headers: { 'x-hasura-role': 'manager' } } },
            )
            .pipe(
              map(
                (res) => !!res.data?.update_comunicazioni_email_template_by_pk,
              ),
              tap((ok) => {
                if (!ok) return;
                _toast.add({
                  key: 'global-toast',
                  severity: 'success',
                  summary: _translate.instant(
                    'sottoscrizione.modelli.aggiornato',
                  ),
                });
              }),
              catchError((error) => {
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
                return of(false);
              }),
            );
        });
      },

      deleteEmailTemplate: (id: string): Observable<boolean> => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(DeleteEmailTemplateGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return _mutation
            .mutate(
              { id },
              { context: { headers: { 'x-hasura-role': 'manager' } } },
            )
            .pipe(
              map(() => true),
              tap(() =>
                _toast.add({
                  key: 'global-toast',
                  severity: 'success',
                  summary: _translate.instant(
                    'sottoscrizione.modelli.eliminato',
                  ),
                }),
              ),
              catchError((error) => {
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant('toast.error.title'),
                  detail: _translate.instant('toast.error.detail', { error }),
                });
                return of(false);
              }),
            );
        });
      },

      // Storico email inviate dall'utente (comunicazioni.email, filtrate per auth_id da Hasura)
      getEmailInviate: (
        limit: number,
        offset: number,
        where: Comunicazioni_Email_Bool_Exp = {},
        orderBy: Comunicazioni_Email_Order_By[] = [{ created_at: Order_By.Desc }],
      ): Observable<{
        rows: GetEmailInviateQuery['comunicazioni_email'];
        total: number;
      }> => {
        return runInInjectionContext(injector, () => {
          const _query = inject(GetEmailInviateGQL);
          return _query
            .fetch(
              { limit, offset, where, order_by: orderBy },
              {
                fetchPolicy: 'network-only',
                context: { headers: { 'x-hasura-role': 'manager' } },
              },
            )
            .pipe(
              map((res) => ({
                rows: res.data?.comunicazioni_email ?? [],
                total: res.data?.comunicazioni_email_aggregate?.aggregate?.count ?? 0,
              })),
              catchError(() => of({ rows: [], total: 0 })),
            );
        });
      },

      sendSms: (cer_id: string, cellulare: string, message: string, stato: string) => {
        return runInInjectionContext(injector, () => {
          const _mutation = inject(InsertSmsGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          return _mutation
            .mutate(
              { cer_id, cellulare, message, stato },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .pipe(
              tapResponse({
                next: () => {
                  _toast.add({
                    key: 'global-toast',
                    severity: 'success',
                    summary: _translate.instant('sottoscrizione.invio.sms_dialog.inviato'),
                  });
                },
                error: (error) => {
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', { error }),
                  });
                },
              }),
            );
        });
      },
    };
  }),
  withHooks({
    onInit(store) {
      effect(() => {
        const cer = store.selected_cer?.();
        if (cer) store.initConfigurazioni(cer.id);
      });

      const getGeomCabina = inject(GetGeomCabinaGQL);
      const getAsset = inject(GetAssetInCabinaIdsGQL);
      const _toast = inject(MessageService);
      const _translate = inject(TranslateService);

      const _getConfigurazioneGeom = inject(GetConfigurazioneGeomGQL);

      effect(() => {
        const geom_id = store.selected_configurazione?.()?.geom?.id;
        if (geom_id)
          _getConfigurazioneGeom
            .fetch(
              {
                id: geom_id,
              },
              {
                context: {
                  headers: { 'x-hasura-role': 'manager' },
                },
              },
            )
            .subscribe({
              next: (res) => {
                if (res.data)
                  patchState(store, {
                    selected_configurazione_geometria: res.data.cer_geom_by_pk,
                  });
              },
              error: (error) => {
                patchState(store, {
                  selected_configurazione_geometria: undefined,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant(`toast.error.title`),
                  detail: _translate.instant(`toast.error.detail`, {
                    error,
                  }),
                });
              },
            });
      });

      effect(() => {
        const cer_cabina = store.selected_configurazione?.()?.cer_cabina;

        if (cer_cabina) {
          const unique = cer_cabina.map((e) => e.cabina.id);
          const validIds = new Set(unique.map(Number));

          let ids = unique.map((cabina_id) => Number(cabina_id));

          const currentAsset = untracked(() =>
            store.selected_configurazione_assets(),
          );
          getAsset.subscribe({ cabina_ids: ids }).subscribe({
            next: (assetRes) => {
              patchState(store, {
                selected_configurazione_assets: [
                  ...currentAsset,
                  ...(assetRes.data?.asset ?? []),
                ],
              });
            },
            error: (error) => {
              patchState(store, {
                failure: error,
              });
              _toast.add({
                key: 'global-toast',
                severity: 'error',
                summary: _translate.instant(`toast.error.title`),
                detail: _translate.instant(`toast.error.detail`, {
                  error,
                }),
              });
            },
          });
        } else {
          patchState(store, {
            selected_configurazione_geometrie: {},
            selected_configurazione_assets: [],
          });
        }
      });
    },
  }),
);
